import axios, { AxiosError, AxiosHeaders, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import i18next from 'i18next';

import { memoizedRefreshedSession } from './refreshTokenQueries';
import {
  getApiVersion,
  getCookie,
  retrieveSessionFromStorage,
  shouldRetryWithRefresh,
  shouldUseCookieAuth
} from '@/_helpers/queryInterceptorHelpers';
import { constants } from '@/constants';

import type { Session } from '@/types/auth';
import type { RetryableAxiosRequestConfig } from '@/types/api';

const env: ImportMetaEnv = import.meta.env;
const apiVersion: string = getApiVersion(env);

const axiosApiInstance: AxiosInstance = axios.create({
  baseURL: constants.apiUrl,
  headers: {
    Accept: `application/vnd.testerly+json; version=${apiVersion}`
  },
  responseType: 'json',
  responseEncoding: 'utf8',
  timeout: 30_000,
});

/**
 * Request interceptor. Appends CSRF, authorization, and language headers.
 */
axiosApiInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const session: Session | null = retrieveSessionFromStorage();
    const isUsingCookieAuth: boolean = shouldUseCookieAuth(env);
    // noinspection SpellCheckingInspection
    const csrfToken: string = getCookie("csrftoken");

    // Ensure the "headers" object exists and is an instance of AxiosHeaders
    config.headers = AxiosHeaders.from(config.headers);

    config.withCredentials = isUsingCookieAuth;

    // Append CSRF token if available and using cookie authentication
    if (csrfToken && isUsingCookieAuth) {
      config.headers.set("X-CSRFToken", csrfToken);
    }

    // Append access token if available and not using cookie authentication
    if (session?.accessToken && !isUsingCookieAuth) {
      config.headers.set("Authorization", `Bearer ${session.accessToken}`);
    }

    // Append language preference
    config.headers.set("Accept-Language", i18next.language);

    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

/**
 * When a response fails with a 401 (unauthorized) error, refresh the session and retry the request.
 */
axiosApiInstance.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => response, async (error: AxiosError<any>): Promise<AxiosResponse> => {
    const config = error.config as RetryableAxiosRequestConfig | undefined;
    const isUsingCookieAuth: boolean = shouldUseCookieAuth(env);

    /**
     * Reject if:
     * - Not a 401 error
     * - The request has already been retried
     * - No Authorization header was originally sent
     */
    if (!shouldRetryWithRefresh(error, config)) {
      return Promise.reject(error);
    }

    /**
     * Retry on 401 with a refresh token
     */

    // Mark the request as retried
    config.sent = true;

    // Whether to use session authentication credentials (via withCredentials)
    config.withCredentials = isUsingCookieAuth;

    // Attempt to refresh the session
    const refreshedSession: Session | null = await memoizedRefreshedSession();
    if (!refreshedSession?.accessToken) {
      return Promise.reject(error);
    }

    // Ensure the "headers" object exists and is an instance of AxiosHeaders
    config.headers = AxiosHeaders.from(config.headers);

    // Update the Authorization header with the new access token
    config.headers.set('Authorization', `Bearer ${refreshedSession.accessToken}`);

    // Retry the request
    return axiosApiInstance.request(config);
  }
);

export const api: AxiosInstance = axiosApiInstance;